import moment from "moment";
import config from "config/config";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AgentData } from "context/Agent/DataProvider";
import { useContext, useEffect, useState } from "react";
import { useRowPagination } from "hooks/useRowPagination";
import { isFlagEnabled, sortDates, filterDates } from "js/utilities";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";

import { ChatContext } from "context/Agent/ChatProvider";
import RowPagination from "components/General/RowPagination";
import MessageRow from "components/MessageCenter/MessageRow";
import PolicyFilter from "components/MessageCenter/PolicyFilter";
import CategoryFilter from "components/MessageCenter/CategoryFilter";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";

export default function CorrespondenceTab({
  selectedPolicyId,
  setSelectedPolicyId,
  selectedCategory,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.user);
  const { agentPolicies } = useContext(AgentData);

  const { openChatWindow, setOpenChatWindow } = useContext(ChatContext);
  const [isLoading, setIsLoading] = useState(true);

  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);

  const [currentCategory, setCurrentCategory] = useState(
    selectedCategory ?? "ALL"
  );
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);

  const { rowsForDisplay: current, rowPaginationProps } = useRowPagination(
    filteredAlerts,
    [3, 10, 25, "all"]
  );

  useEffect(() => {
    setCurrentCategory(selectedCategory ?? "ALL");
  }, [selectedCategory]);

  // get correspondense, setup master alerts, setup categories and filter alerts by category
  useEffect(() => {
    setIsLoading(true);

    let policySelectionArrayForDocTab = [];
    agentPolicies.map((p) => {
      let pry = p?.parties?.find(
        (x) => x.person?.type?.toLowerCase() === "primary owner"
      );
      policySelectionArrayForDocTab.push({
        value: p.oldPolicy,
        displayAs: `${p.oldPolicy}: ${p?.insuredFullName}`,
        displayAsValue: `${p.oldPolicy}`,
        ...p,
      });
      setPolicyFilterOptions([
        {
          value: "all",
          displayAs: "All Contracts",
          displayAsValue: "All Contracts",
        },
        ...policySelectionArrayForDocTab,
      ]);
    });

    dispatch(
      getCorrespondenceList({
        agentId: user?.agentViewingId,
        policyId: "",
        beginDate: moment()
          .subtract(parseInt(config.ALERT_CENTER_DURATION), "months")
          .format("MM/DD/YYYY"),
        endDate: moment().format("MM/DD/YYYY"),
        agentPolicies: agentPolicies,
      })
    ).then((response) => {
      setIsLoading(false);
      if (response?.meta?.requestStatus === "fulfilled") {
        let alertArray = [];
        response.payload.allAlerts.forEach((alert) => {
          // exclude policy alerts on this tab
          if (alert?.type !== "alert") {
            // Hide all commissions documents in A&D page [4004]
            if (
              alert?.document?.typeOfDocument?.toUpperCase() === "COMMISSIONS"
            )
              return;

            // Get alerts that are in range of ALERT_CENTER_DURATION (12 months)
            if (
              filterDates(
                alert,
                "documentReceiveDate",
                config.ALERT_CENTER_DURATION
              )
            ) {
              // for all other correspondence
              // make sure that the current alert is in agentPolicies array
              const currentPolicy = agentPolicies.find(
                (policy) => policy?.contractID === alert?.policy?.contractID
              );

              if (!currentPolicy) return;

              alertArray.push({
                oldPolicy: currentPolicy.oldPolicy,
                ...alert,
                document: {
                  ...alert.document,
                  typeOfDocument: alert?.document?.PortalDisplayName,
                },
              });
            }
          }
        });
        const sortedAlertsArray = sortDates(
          alertArray,
          "document",
          "dateOfDocument"
        );
        setAlerts(sortedAlertsArray);
        setFilteredAlerts(sortedAlertsArray);

        const uniqueCategories = new Set();
        const categoriesList = response.payload.allCategories.reduce(
          (acc, category) => {
            if (category.typeOfDocument !== "COMMISSIONS") {
              const typeOfDocument = category?.PortalDisplayName;
              if (!uniqueCategories.has(typeOfDocument)) {
                uniqueCategories.add(typeOfDocument);
                acc.push({
                  ...category,
                  typeOfDocument,
                });
              }
            }
            return acc;
          },
          [{ typeOfDocument: "ALL", description: "All messages in your inbox" }]
        );

        setCategories(categoriesList);
        setFilteredCategories(categoriesList);
        setSelectedPolicyId({
          value:
            typeof selectedPolicyId === "object"
              ? selectedPolicyId?.value || "all"
              : selectedPolicyId,
        });
      } else {
        if (
          response.payload[0] ===
          t("AlertsCorrespondence-correspondence-error-message-1")
        ) {
          setAlerts([]);
          setFilteredAlerts([]);
          setCategories((currentCategories) => [...currentCategories]);
          setFilteredCategories((currentCategories) => [...currentCategories]);
        }
      }
    });
  }, [agentPolicies]);

  const getFilteredAlerts = (categoryType) => {
    if (categoryType === "ALL") setFilteredAlerts(alerts);
    else {
      let result = filteredAlerts.filter((alert) => {
        return alert.document.typeOfDocument === categoryType;
      });
      setFilteredAlerts(result);
    }
  };

  useEffect(() => {
    let shadowAlerts = alerts;
    // filter by selected category
    if (currentCategory !== "ALL")
      shadowAlerts = shadowAlerts.filter(
        (alert) =>
          alert?.document?.typeOfDocument.toLowerCase() ===
          currentCategory?.toLowerCase()
      );
    // filter by selected policy

    if (
      (typeof selectedPolicyId === "string" && selectedPolicyId !== "all") ||
      (typeof selectedPolicyId === "object" &&
        selectedPolicyId?.value !== "all")
    )
      shadowAlerts = shadowAlerts.filter(
        (alert) =>
          alert?.oldPolicy ===
          (typeof selectedPolicyId === "object"
            ? selectedPolicyId?.value
            : selectedPolicyId)
      );
    setFilteredAlerts(shadowAlerts);
  }, [selectedPolicyId, currentCategory, alerts]);

  useEffect(() => {
    let filteredCategories = [
      { typeOfDocument: "ALL", description: "All messages in your inbox" },
      ...categories.filter((category) => {
        if (
          category?.typeOfDocument === "ALL" ||
          category?.typeOfDocument === "COMMISSIONS"
        )
          return false;
        return alerts.some((alert) => {
          const matchesCategory =
            alert?.document?.typeOfDocument === category?.typeOfDocument;
          const matchesPolicy =
            selectedPolicyId?.value === "all" ||
            alert.oldPolicy ===
              (typeof selectedPolicyId === "object"
                ? selectedPolicyId?.value
                : selectedPolicyId);
          return matchesCategory && matchesPolicy;
        });
      }),
    ];
    setFilteredCategories(filteredCategories);

    const isCurrentCategoryAvailable = filteredCategories.some(
      (category) => category?.typeOfDocument === currentCategory
    );
    if (!isCurrentCategoryAvailable && alerts.length > 0) {
      setCurrentCategory("ALL");
    }
  }, [selectedPolicyId]);

  // left this here for legacy PolicyFilter support can be remove along with "ENABLE_AUTOCOMPLETE_COMBOBOX"
  const onchange = (policyId) => {
    let filterAlertArray = alerts;
    if (policyId && policyId !== "")
      filterAlertArray = filterAlertArray.filter(
        (alert) => alert?.oldPolicy === policyId
      );
    if (currentCategory !== "ALL")
      filterAlertArray = filterAlertArray.filter(
        (alert) =>
          alert?.document?.typeOfDocument.toLowerCase() ===
          currentCategory?.toLowerCase()
      );
    setFilteredAlerts(filterAlertArray);
    setSelectedPolicyId(policyId);
  };

  return (
    <EverlakeLoadingOverlay
      isLoading={isLoading}
      loadingText={t("AlertsCorrespondence-correspondence-loading-text")}
    >
      <div className="grid grid-cols-1 gap-4 items-start md:gap-2 md:grid-cols-4">
        <div className="my-6 md:block md:col-span-1">
          {isFlagEnabled("ENABLE_AUTOCOMPLETE_COMBOBOX") ? (
            <div className="mb-3">
              <AgentAutoCompleteComboBox
                labelName="Contract Number"
                inputPlaceholderText={t(
                  "AlertsCorrespondence-input-placeholder"
                )}
                options={policyFilterOptions}
                invalidMessage={t("AlertsCorrespondence-input-invalid-msg-1")}
                selectedOption={selectedPolicyId}
                setSelectedOption={setSelectedPolicyId}
              />
            </div>
          ) : (
            <PolicyFilter
              policyIds={policyFilterOptions.map((option) => option.value)}
              selectedPolicyId={selectedPolicyId}
              setSelectedPolicyId={setSelectedPolicyId}
              currentCategory={currentCategory}
              onchange={onchange}
            />
          )}
          {filteredCategories.map((category, categoryIndex) => {
            return (
              <CategoryFilter
                key={`message-category-${category.typeOfDocument}-${categoryIndex}`}
                category={category}
                first={categoryIndex === 0}
                last={filteredCategories.length - 1 === categoryIndex}
                currentCategory={currentCategory}
                setCurrentCategory={setCurrentCategory}
                getFilteredAlerts={getFilteredAlerts}
              />
            );
          })}
        </div>
        <div className="md:ml-4 xl:ml-6 md:col-span-3 mt-12">
          <div className="md:ml-4 xl:ml-6 text-xs font-semibold text-center md:col-span-4">
            <Trans
              i18nKey={"AlertsCorrespondence-agentdisclaimer"}
              components={{
                button1: (
                  <button
                    onClick={() => setOpenChatWindow(!openChatWindow)}
                    className="ui-btn-hyperlink text-xs font-bold"
                  ></button>
                ),
              }}
            ></Trans>
          </div>
          <div className="mt-8">
            {/* <!-- Display Correspondence list --> */}
            {!current || current.length === 0 ? (
              <div className="w-full text-center">
                <h3>{t("AlertsCorrespondence-correspondence-text-1")}</h3>
              </div>
            ) : (
              current.map((message, idx) => (
                <MessageRow
                  key={`message - ${message.document.name} - ${idx}`}
                  hasDescription={false}
                  message={message}
                  policy={agentPolicies.find(
                    (p) => p.oldPolicy === message.oldPolicy
                  )}
                />
              ))
            )}

            {/* <!-- paging component --> */}
            {current.length > 0 && <RowPagination {...rowPaginationProps} />}
          </div>
        </div>
      </div>
    </EverlakeLoadingOverlay>
  );
}
